import React, { useEffect, useRef, useState } from "react";
import {
  EditorState,
  RichUtils,
  convertToRaw,
  ContentState,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaCode,
  FaSuperscript,
  FaSubscript,
  FaListUl,
  FaListOl,
  FaIndent,
  FaOutdent,
  FaLink,
  FaUnlink,
} from "react-icons/fa";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "../Clients/clientChatBox.css";
import "./emailRichTextEditor.css";

const ToolbarButton = ({ icon: Icon, label, active, onClick }) => (
  <button
    type="button"
    className={`client-chatbox-toolbar-btn${active ? " is-active" : ""}`}
    onClick={onClick}
    aria-label={label}
    title={label}
  >
    <Icon className="client-chatbox-toolbar-icon" aria-hidden="true" />
  </button>
);

const EmailRichTextToolbar = ({ editorState, onChange }) => {
  const selection = editorState.getSelection();
  const currentStyle = editorState.getCurrentInlineStyle();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  const toggleInline = (style) => {
    onChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlock = (blockTypeName) => {
    onChange(RichUtils.toggleBlockType(editorState, blockTypeName));
  };

  const adjustDepth = (delta) => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const blockKey = selection.getStartKey();
    const block = content.getBlockForKey(blockKey);
    const blockTypeName = block.getType();

    if (
      blockTypeName !== "unordered-list-item" &&
      blockTypeName !== "ordered-list-item"
    ) {
      return;
    }

    const currentDepth = block.getDepth();
    const nextDepth = Math.max(0, Math.min(4, currentDepth + delta));
    if (nextDepth === currentDepth) return;

    const nextBlock = block.set("depth", nextDepth);
    const nextBlockMap = content.getBlockMap().set(blockKey, nextBlock);
    const nextContent = content.merge({
      blockMap: nextBlockMap,
      selectionAfter: selection,
    });

    onChange(EditorState.push(editorState, nextContent, "adjust-depth"));
  };

  const addLink = () => {
    const url = window.prompt("Enter link URL");
    if (!url?.trim()) return;

    const contentState = editorState.getCurrentContent();
    const contentWithEntity = contentState.createEntity("LINK", "MUTABLE", {
      url: url.trim(),
      targetOption: "_blank",
    });
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    let nextState = EditorState.set(editorState, {
      currentContent: contentWithEntity,
    });
    nextState = RichUtils.toggleLink(nextState, selection, entityKey);
    onChange(nextState);
  };

  const removeLink = () => {
    onChange(RichUtils.toggleLink(editorState, selection, null));
  };

  return (
    <div className="client-chatbox-custom-toolbar bg-light border-bottom">
      <ToolbarButton
        icon={FaBold}
        label="Bold"
        active={currentStyle.has("BOLD")}
        onClick={() => toggleInline("BOLD")}
      />
      <ToolbarButton
        icon={FaItalic}
        label="Italic"
        active={currentStyle.has("ITALIC")}
        onClick={() => toggleInline("ITALIC")}
      />
      <ToolbarButton
        icon={FaUnderline}
        label="Underline"
        active={currentStyle.has("UNDERLINE")}
        onClick={() => toggleInline("UNDERLINE")}
      />
      <ToolbarButton
        icon={FaStrikethrough}
        label="Strikethrough"
        active={currentStyle.has("STRIKETHROUGH")}
        onClick={() => toggleInline("STRIKETHROUGH")}
      />
      <ToolbarButton
        icon={FaCode}
        label="Monospace"
        active={currentStyle.has("CODE")}
        onClick={() => toggleInline("CODE")}
      />
      <ToolbarButton
        icon={FaSuperscript}
        label="Superscript"
        active={currentStyle.has("SUPERSCRIPT")}
        onClick={() => toggleInline("SUPERSCRIPT")}
      />
      <ToolbarButton
        icon={FaSubscript}
        label="Subscript"
        active={currentStyle.has("SUBSCRIPT")}
        onClick={() => toggleInline("SUBSCRIPT")}
      />
      <ToolbarButton
        icon={FaListUl}
        label="Bullet list"
        active={blockType === "unordered-list-item"}
        onClick={() => toggleBlock("unordered-list-item")}
      />
      <ToolbarButton
        icon={FaListOl}
        label="Numbered list"
        active={blockType === "ordered-list-item"}
        onClick={() => toggleBlock("ordered-list-item")}
      />
      <ToolbarButton
        icon={FaIndent}
        label="Increase indent"
        onClick={() => adjustDepth(1)}
      />
      <ToolbarButton
        icon={FaOutdent}
        label="Decrease indent"
        onClick={() => adjustDepth(-1)}
      />
      <ToolbarButton icon={FaLink} label="Add link" onClick={addLink} />
      <ToolbarButton icon={FaUnlink} label="Remove link" onClick={removeLink} />
    </div>
  );
};

const valueToEditorState = (value) => {
  if (!value || value === "<p></p>") {
    return EditorState.createEmpty();
  }

  if (/<[a-z][\s\S]*>/i.test(value)) {
    try {
      const blocksFromHtml = htmlToDraft(value);
      if (blocksFromHtml?.contentBlocks?.length) {
        return EditorState.createWithContent(
          ContentState.createFromBlockArray(
            blocksFromHtml.contentBlocks,
            blocksFromHtml.entityMap,
          ),
        );
      }
    } catch {
      // Fall through to plain text handling.
    }
  }

  return EditorState.createWithContent(ContentState.createFromText(value));
};

export const isEmptyEmailHtml = (html) => {
  if (!html) return true;
  const stripped = html
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]+>/g, "")
    .trim();
  return !stripped;
};

const preventFileDragDefault = (e) => {
  if (e.dataTransfer?.types?.includes("Files")) {
    e.preventDefault();
  }
};

const EmailRichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Please Enter",
  blockFileDrag = false,
}) => {
  const [editorState, setEditorState] = useState(() =>
    valueToEditorState(value),
  );
  const lastEmittedHtml = useRef(value);

  useEffect(() => {
    if (value !== lastEmittedHtml.current) {
      setEditorState(valueToEditorState(value));
      lastEmittedHtml.current = value;
    }
  }, [value]);

  const handleEditorChange = (state) => {
    setEditorState(state);
    const html = draftToHtml(convertToRaw(state.getCurrentContent()));
    lastEmittedHtml.current = html;
    onChange?.(html);
  };

  const fileDragHandlers = blockFileDrag
    ? {
        onDragEnter: preventFileDragDefault,
        onDragOver: preventFileDragDefault,
        onDrop: preventFileDragDefault,
      }
    : {};

  return (
    <div
      className="email-rich-text-editor client-chatbox-editor-wrap border rounded bg-white"
      {...fileDragHandlers}
    >
      <EmailRichTextToolbar
        editorState={editorState}
        onChange={handleEditorChange}
      />
      <Editor
        editorState={editorState}
        placeholder={placeholder}
        onEditorStateChange={handleEditorChange}
        toolbarHidden
        wrapperClassName="p-0"
        editorClassName="p-3 client-chatbox-editor-input email-rich-text-editor__input"
      />
    </div>
  );
};

export default EmailRichTextEditor;
